import { useContext, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { TieredMenu } from "primereact/tieredmenu";
import { Skeleton } from "primereact/skeleton";
import { ContactProfileDetailsContext } from "app/uicomponents/macrocomponents/ContactLayout";
import TruncatedText from "app/uicomponents/microcomponents/TruncatedText";
import SignoutLeftPanel from "app/uicomponents/microcomponents/SignoutLeftPanel";
import HexagonAvatar from "../features/dashboard/components/HeaxagonImage";

const CONTACT_PROFILE_ROUTE = "/agent/contact/profile";

/**
 * Client profile block at the bottom of the contact nav bar: avatar, and - when the bar is
 * expanded - the client's name with their role beneath it. Clicking either opens a profile
 * menu whose last entry is Logout, mirroring the advisor nav bar (DashboardLeftPanel).
 *
 * Replaces the standalone "Sign Out" link the contact bar used to carry below the avatar.
 */
const ContactProfileMenu = () => {
  const { contactProfilePic, isVerified, contactFirstName, contactLastName, isOpen } = useContext(
    ContactProfileDetailsContext,
  );
  const navigate = useNavigate();
  const menuRef = useRef<TieredMenu>(null);

  const clientName = [contactFirstName, contactLastName].filter(Boolean).join(" ");

  const profileItems = [
    {
      label: "Profile",
      icon: "pi pi-user inherit-menuIcon-color",
      command: () => navigate(CONTACT_PROFILE_ROUTE),
    },
    {
      separator: true,
    },
    {
      // Reuses the contact portal's existing sign-out component, which carries its own
      // confirm dialog and logout dispatch, rather than reimplementing that flow here.
      label: "Logout",
      template: () => <SignoutLeftPanel asMenuItem />,
      className: "logout-btn",
    },
  ];

  return (
    <>
      <div className={`${isOpen ? "flex items-center" : "flex justify-center"} my-4`}>
        <div
          className="flex-shrink-0 cursor-pointer"
          onClick={(e) => menuRef.current?.toggle(e)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") menuRef.current?.toggle(e);
          }}
        >
          <HexagonAvatar
            imageUrl={contactProfilePic}
            size={58}
            isVerified={!!isVerified}
            pointTop
          />
        </div>
        {isOpen && (
          <div
            className="conthrax-font ml-3 min-w-0 flex-1 cursor-pointer"
            onClick={(e) => menuRef.current?.toggle(e)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") menuRef.current?.toggle(e);
            }}
          >
            {clientName ? (
              <TruncatedText
                text={clientName}
                tooltipId={`contact-sidebar-${clientName}`}
                tooltipPosition="top"
                maxWidth="max-w-full"
              />
            ) : (
              <Skeleton className="mb-2" width="5rem" height="15px" />
            )}
            {clientName ? (
              <p className="truncate max-w-full text-[10px]">Client</p>
            ) : (
              <Skeleton width="3rem" height="10px" />
            )}
          </div>
        )}
      </div>
      <TieredMenu model={profileItems} popup ref={menuRef} breakpoint="767px" />
    </>
  );
};

export default ContactProfileMenu;
